import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useEffect } from 'react';
import styles from './VotingSession.module.css';
import classNames from 'classnames';
import type VotingSessionWidget from '../../../widgets/VotingSessionWidget';
import VotingSessionController from './controllers/VotingSessionController';
import { pluralize } from '@aha-app/aha-shared';
import DeleteConfirmation from './DeleteConfirmation';
import { formattedTimestamp } from '../../../utils/time';
import StandardVotingSessionResults from './StandardVotingSessionResults';
import FistToFiveVotingSessionResults from './FistToFiveVotingSessionResults';
import PokerVotingSessionResults from './PokerVotingSessionResults';
import { VotingSessionType } from '../../../widgets/VotingSessionType';
import { VotingSessionNameAndIcon } from './VotingSetupSessionTypeControls';

const ResultsPanel: React.FC<{ widget: VotingSessionWidget }> = ({
  widget,
}) => {
  const controller = useController(VotingSessionController);
  const author = controller.getResolvedUser(widget.authorId);

  const voteCount = widget.totalVotes;
  const voterCount = widget.totalVoters;

  const showVoteCount = widget.sessionType === VotingSessionType.Standard;

  const [deletingSession, setDeletingSession] = React.useState(false);

  useEffect(() => {
    controller.widgetsController.actionSelectVotingSessionWidget(widget);

    return () => {
      controller.widgetsController.actionSelectVotingSessionWidget(null);
    };
  }, [controller.widgetsController, widget]);

  if (deletingSession) {
    return (
      <div className='side-panel__content sidepanel-scroll-parent'>
        <DeleteConfirmation
          widget={widget}
          onConfirm={() => {
            controller.actionPopSidePanel();
            controller.actionRemoveVotingSession(widget);
            setDeletingSession(false);
          }}
          onCancel={() => {
            setDeletingSession(false);
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className='side-panel__header'>
        <div
          className={classNames(
            'side-panel__title gap-4 mb-3',
            styles.votingResultTitle
          )}
        >
          <h3 className='h-300 side-panel__title-text mb-0'>{widget.name}</h3>
          <aha-button
            kind='ghost'
            class='px-3 py-2'
            onClick={() => {
              setDeletingSession(true);
            }}
          >
            <aha-icon icon='fa-regular fa-trash' />
          </aha-button>
        </div>
        <div className='side-panel__description flex-col gap-2 mb-3'>
          <div className='side-panel__meta-text t-300 gap-2 color-tertiary'>
            <VotingSessionNameAndIcon
              sessionType={widget.sessionType}
              size='small'
            />
          </div>
          <div className='side-panel__meta-text t-300'>
            Created by <span className='t-200 color-tertiary'>{author}</span>
          </div>
          <div className='side-panel__meta-text t-300 flex-row flex-row--justify-space-between'>
            <span>{formattedTimestamp(widget.startedAt)}</span>
            <span>
              {showVoteCount && (
                <span>
                  <span className='t-200 color-tertiary'>{voteCount}</span>{' '}
                  {pluralize(voteCount, 'vote')}
                </span>
              )}
              <span className='ml-2'>
                <span className='t-200 color-tertiary'>{voterCount}</span>{' '}
                {pluralize(voterCount, 'participant')}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className='side-panel__content sidepanel-scroll-parent'>
        {widget.sessionType === VotingSessionType.Standard && (
          <StandardVotingSessionResults widget={widget} />
        )}
        {widget.sessionType === VotingSessionType.FistToFive && (
          <FistToFiveVotingSessionResults widget={widget} />
        )}
        {widget.sessionType === VotingSessionType.Poker && (
          <PokerVotingSessionResults widget={widget} showHeader={false} />
        )}
      </div>
    </>
  );
};

export default ApplicationView(ResultsPanel);
